import { Fragment, h } from '@stencil/core';
import { i18n } from 'i18n';
import reschedulingModal from 'store/reschedulingModal';
import Heading from 'eva-components/Heading';
import EvaMessage from 'eva-components/Message';
import { ButtonType, ClusterRoute, ClusterRouteSegment } from 'client/types';
import state from 'store';
import { triggerSubmitAction, triggerSubmitChatMessage, triggerTrackEvent } from 'utils/events';

export default function ReschedulingModal({ show }: { show: boolean }) {
  const handleClick = () => {
    reschedulingModal.showOriginal = !reschedulingModal.showOriginal;
  };

  const onClose = () => {
    state.showFullScreenModal = null;
  };

  const onAccept = () => {
    state.showFullScreenModal = null;
    reschedulingModal.accepted = true;

    if (typeof state.chatId === 'string' && typeof reschedulingModal.rescheduling.action?.actionId === 'string') {
      triggerSubmitChatMessage({
        message: i18n.t.boards.reschedulingCard.modal.waitingAction,
        type: 'ASSISTANT',
        from: 'USER',
        method: 'TEXT',
      });

      triggerSubmitAction({
        action: {
          actionId: reschedulingModal.rescheduling.action.actionId,
          type: ButtonType.ACTION,
        },
      });

      triggerTrackEvent({
        event_type: 'user',
        event_name: 'rescheduling_action_accept',
        data: {
          actionId: reschedulingModal.rescheduling.action.actionId,
        },
      });
    }
  };

  function getFlightItem(segment: ClusterRouteSegment, route: ClusterRoute) {
    return (
      <Fragment>
        {segment.layover_duration && (
          <div class="card-waiting-time -eva-3-p-sm -eva-3-mt-lg -eva-3-mb-lg">
            <i class="eva-3-icon-time -eva-3-icon-xsm -eva-3-tc-gray-2"></i>
            <span class="eva-3-body-2 layover-text">
              {i18n.t.boards.reschedulingCard.modal.layoverWaiting}
              <span class="-eva-3-bold"> {segment.layover_duration} </span>
              {i18n.t.boards.reschedulingCard.modal.layoverIn + ' ' + segment.from.city.name}
            </span>
          </div>
        )}
        <div class="eva-3-card -eva-3-shadow-line content -eva-3-mb-lg">
          <div class="card-header -eva-3-p-md">
            <div class="airline-name">
              <img src={segment.marketing_carrier.logo} />
              <Heading variant="caption" text={segment.marketing_carrier.name} className="-eva-3-bold" />
            </div>
            <div class="airline-number">
              <Heading variant="caption" text={`${i18n.t.boards.reschedulingCard.modal.flightNumber} ${segment.flight_number}`} />
            </div>
          </div>
          <div class="card-body -eva-3-p-md">
            <div class="body-description">
              <Heading variant="caption" tag="div" text={segment.departure.date} className="date -eva-3-mb-xsm" />
              <Heading variant="h3" tag="h3" text={segment.departure.time} className="-eva-3-mb-xsm" />
              <Heading variant="overline-1" tag="div" text={segment.from.airport.code} />
              <Heading variant="caption" tag="p" text={segment.from.city.name} className="-eva-3-mb-sm" />
              <Heading variant="caption" tag="p" text={segment.from.airport.name} />
            </div>
            <div class="body-scales -eva-3-pt-lg">
              <Heading variant="body-2" tag="span" text={route.stops === 0 ? i18n.t.boards.reschedulingCard.nonstop : i18n.t.boards.reschedulingCard.duration} />
              <Heading variant="caption" tag="span" text={segment.duration} />
            </div>
            <div class="body-description">
              <div class={`date ${segment.plus_days ? '-eva-3-tc-red-3' : ''}`}>
                {segment.plus_days && <Heading text={`+${segment.plus_days}`} variant="caption" />}
                <Heading variant="caption" tag="div" text={segment.arrival.date} />
              </div>
              <Heading variant="h3" tag="h3" text={segment.arrival.time} className="-eva-3-mb-xsm" />
              <Heading variant="overline-1" tag="div" text={segment.to.airport.code} />
              <Heading variant="caption" tag="p" text={segment.to.city.name} className="-eva-3-mb-sm" />
              <Heading variant="caption" tag="p" text={segment.to.airport.name} />
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return show ? (
    <div id="rescheduling-modal" role="presentation" class={'eva-3-modal -smooth-scroll fullscreen-modal -show-modal -show-sticky-footer'}>
      <div class="modal-header -eva-3-p-md" role="presentation">
        <div class="modal-title-container">
          <h4 class="eva-3-h4 modal-title">{i18n.t.boards.reschedulingCard.modal.title}</h4>
        </div>
        <i
          class="eva-3-icon-cross modal-close -eva-3-mr-md"
          role="button"
          tabindex="0"
          aria-label={i18n.t.chat.message.feedback.close}
          onClick={() => {
            onClose();
          }}
        ></i>
      </div>
      <div class="modal-content" role="presentation">
        {reschedulingModal.rescheduling.alternative.confirmation_message && (
          <EvaMessage
            description={reschedulingModal.rescheduling.alternative.confirmation_message.message}
            type={reschedulingModal.rescheduling.alternative.confirmation_message.level}
            className="-eva-3-mb-md"
          />
        )}
        {reschedulingModal.rescheduling.alternative.cluster.routes.map(route => (
          <Fragment>
            <div class="content-header -eva-3-mb-md">
              <i class={`eva-3-icon-airplane-${route.header.icon} -eva-3-icon-xsm -eva-3-mr-sm`}></i>
              <Heading text={route.header.title} variant={'h4'} />
            </div>
            {route.segments.map(segment => getFlightItem(segment, route))}
            <div class="card-total-duration -eva-3-p-sm -eva-3-mt-lg -eva-3-mb-lg">
              <Heading text={i18n.t.boards.reschedulingCard.modal.totalDuration + ':'} variant={'body-2'} />
              <span class="-eva-3-bold">{route.duration}</span>
            </div>
            <Heading variant="caption" tag="div" text={i18n.t.boards.reschedulingCard.modal.localDateDisclaimer} className="local-date-disclaimer -eva-3-mt-md -eva-3-mb-xxlg" />
          </Fragment>
        ))}
        {reschedulingModal.rescheduling.original && (
          <div class="eva-3-dropdown">
            <div class={`dropdown-item ${reschedulingModal.showOriginal ? '-active' : ''}`}>
              <div class="dropdown-item-container" onClick={() => handleClick()}>
                <span class="item-label">
                  {reschedulingModal.showOriginal ? i18n.t.boards.reschedulingCard.modal.hideOriginalFlight : i18n.t.boards.reschedulingCard.modal.showOriginalFlight}
                </span>
                <i class="eva-3-icon-chevron-down dropdown-icon"></i>
              </div>
              <div class="dropdown-subcontent -eva-3-mt-lg">
                {reschedulingModal.rescheduling.original.cluster.routes.map(route => (
                  <Fragment>
                    <div class="content-header -eva-3-mb-md">
                      <i class={`eva-3-icon-airplane-${route.header.icon} -eva-3-icon-xsm -eva-3-mr-sm`}></i>
                      <Heading text={route.header.title} variant={'h4'} />
                    </div>
                    {route.segments.map(segment => getFlightItem(segment, route))}
                    <div class="card-total-duration -eva-3-p-sm -eva-3-mt-lg -eva-3-mb-lg">
                      <Heading text={i18n.t.boards.reschedulingCard.modal.totalDuration + ':'} variant={'body-2'} />
                      <span class="-eva-3-bold">{route.duration}</span>
                    </div>
                    <Heading
                      variant="caption"
                      tag="div"
                      text={i18n.t.boards.reschedulingCard.modal.localDateDisclaimer}
                      className="local-date-disclaimer -eva-3-mt-md -eva-3-mb-xxlg"
                    />
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <div class="modal-footer">
        <div class="footer">
          <button type="button" class="eva-3-btn -lg -primary" onClick={() => onAccept()} disabled={reschedulingModal.accepted}>
            <em class="btn-text">{i18n.t.boards.reschedulingCard.modal.buttonConfirmation}</em>
          </button>
        </div>
      </div>
    </div>
  ) : null;
}
